import React from "react";
import { Package } from "lucide-react";

import ChildModuleCard from "./ChildModuleCard";
import { MODULE_ICONS } from "../constants/constant";

const ModuleCard = ({
  module,
  type = "core",
  subscribedBaseModuleDetails = [],
  selectedCoreModuleId,
  toggleBaseModule,
  toggleChildModule,
  toggleSubModule,
  toggleAllChildModules,
  toggleAllSubModules,
}) => {
  console.log(
    "subscribedBaseModuleDetails ModuleCard",
    subscribedBaseModuleDetails,
  );
  const selectedBaseModule = subscribedBaseModuleDetails.find(
    (item) => item.BASEMODULE_ID === module.BASEMODULE_ID,
  );

  const isSelected = !!selectedBaseModule;

  const isCore = module.BASEMODULE_TYPE === "CORE";

  const Icon =
    MODULE_ICONS?.[
      module.BASEMODULE_NAME?.replace(/\s+/g, "_")?.toUpperCase()
    ] || Package;

  // =========================
  // THEME
  // =========================

  const theme =
    type === "addon"
      ? {
          iconBg: "bg-violet-100 dark:bg-violet-500/15",

          iconColor: "text-violet-700 dark:text-violet-300",

          selectedCard:
            "border-violet-300 dark:border-violet-500/40 bg-violet-50 dark:bg-violet-500/10",

          badge:
            "bg-violet-100 dark:bg-violet-500/15 text-violet-700 dark:text-violet-300",

          check:
            "bg-violet-600 border-violet-600 dark:bg-violet-500 dark:border-violet-500",

          button:
            "text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-500/10",

          section: "bg-violet-50/50 dark:bg-violet-500/5",
        }
      : {
          iconBg: "bg-teal-100 dark:bg-teal-500/15",

          iconColor: "text-teal-700 dark:text-teal-300",

          selectedCard:
            "border-teal-300 dark:border-teal-500/40 bg-teal-50 dark:bg-teal-500/10",

          badge:
            "bg-teal-100 dark:bg-teal-500/15 text-teal-700 dark:text-teal-300",

          check:
            "bg-teal-600 border-teal-600 dark:bg-teal-500 dark:border-teal-500",

          button:
            "text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-500/10",

          section: "bg-teal-50/40 dark:bg-teal-500/5",
        };

  // =========================
  // VERIFY ALL CHILD SELECTED
  // =========================

  const allChildrenSelected =
    module.MODULES_DATA?.length > 0 &&
    module.MODULES_DATA.every((child) =>
      selectedBaseModule?.SUBSCRIBED_MODULE_DETAILS?.some(
        (item) => item.MODULE_ID === child.MODULE_ID,
      ),
    );

  // =========================
  // HIDE OTHER CORE MODULES
  // =========================

  const hidden = type === "core" && !isSelected && selectedCoreModuleId;

  if (hidden) return null;

  return (
    <div
      className={`
        group rounded-2xl border overflow-hidden
        transition-all duration-300

        ${
          isSelected
            ? `${theme.selectedCard} shadow-md`
            : `
              border-slate-200 dark:border-slate-700
              bg-white dark:bg-slate-900
              hover:border-slate-300 dark:hover:border-slate-600
              hover:shadow-lg
            `
        }
      `}
    >
      {/* ================= HEADER ================= */}

      <div onClick={toggleBaseModule} className="p-5 cursor-pointer">
        <div className="flex gap-4">
          {/* ICON */}

          <div
            className={`
              w-11 h-11 rounded-2xl
              flex items-center justify-center
              flex-shrink-0
              transition-transform duration-300
              group-hover:scale-105
              ${theme.iconBg}
            `}
          >
            <Icon className={`w-5 h-5 ${theme.iconColor}`} />
          </div>

          {/* BODY */}

          <div className="flex-1 min-w-0">
            {/* TITLE */}

            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {module.BASEMODULE_NAME}
              </h4>

              {isCore && (
                <span
                  className="
                    text-[10px]
                    px-2 py-0.5
                    rounded-full
                    font-medium
                    bg-amber-100
                    dark:bg-amber-500/15
                    text-amber-700
                    dark:text-amber-300
                  "
                >
                  Required
                </span>
              )}

              {module.MODULES_DATA?.length > 0 && (
                <span
                  className={`
                    text-[10px]
                    px-2 py-0.5
                    rounded-full
                    font-medium
                    ${theme.badge}
                  `}
                >
                  {module.MODULES_DATA.length} modules
                </span>
              )}
            </div>

            {/* DESCRIPTION */}

            <p
              className="
                text-xs leading-relaxed mt-1.5
                text-slate-500 dark:text-slate-400
                line-clamp-2
              "
            >
              {module.BASEMODULE_DESCRIPTION ||
                "Module description not available"}
            </p>

            {/* PRICE */}

            <div className="mt-3 flex items-end gap-1">
              <span className="text-2xl font-bold text-slate-900 dark:text-white">
                ${module.BASEMODULE_BASEPRICE}
              </span>

              <span className="text-xs mb-1 text-slate-500 dark:text-slate-400">
                /month
              </span>
            </div>
          </div>

          {/* CHECK */}
          <div
            className={`
              w-5 h-5 rounded-full border-2
              flex items-center justify-center
              transition-all duration-300
              mt-1 flex-shrink-0

              ${
                isSelected
                  ? `${theme.check} scale-110`
                  : `
                    border-slate-300 dark:border-slate-600
                    bg-white dark:bg-slate-900
                  `
              }
            `}
          >
            {isSelected && (
              <svg
                className="w-2.5 h-2.5 text-white"
                fill="none"
                viewBox="0 0 10 10"
              >
                <path
                  d="M1.5 5l2.5 2.5 5-5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        </div>
      </div>

      {/* ================= CHILD MODULES ================= */}

      {isSelected && module.MODULES_DATA?.length > 0 && (
        <div
          className={`
              border-t
              border-slate-200 dark:border-slate-700
              p-4
              ${theme.section}
            `}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                Included modules
              </p>

              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                Select at least one module
              </p>
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleAllChildModules(module);
              }}
              className={`
                  text-[11px]
                  font-medium
                  px-3 py-1.5
                  rounded-lg
                  transition-all duration-200
                  ${theme.button}
                `}
            >
              {allChildrenSelected ? "Deselect all" : "Select all"}
            </button>
          </div>

          <div className="flex flex-col gap-3">
            {module.MODULES_DATA.map((child) => (
              <ChildModuleCard
                key={child.MODULE_ID}
                module={module}
                child={child}
                selectedBaseModule={selectedBaseModule}
                toggleChildModule={toggleChildModule}
                toggleSubModule={toggleSubModule}
                toggleAllSubModules={toggleAllSubModules}
                type={type}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModuleCard;
